/* BOBBO semantic measurement. No auto-start: installation explicitly opts in.
 * Uses existing Google tag only. Never configures/loads a base tag or ecommerce.
 * Data is best effort, memory-only; failures are dropped, never replayed later. */
(function (root, factory) {
  if (typeof module === "object" && module.exports)
    module.exports = factory(require("./bobbo-measurement-core.js"));
  else root.BobboMeasurementAPI = factory(root.BobboMeasurementCore);
})(typeof globalThis === "object" ? globalThis : this, function (core) {
  "use strict";
  // Keep the previous page-type contract; the version distinguishes the new layout.
  const MAP = {
    wall_home: ['hero', 'shapes', 'art_series', 'surfboard_series', 'lifestyle', 'rocket_series', 'newsletter'].map(id => [id, '[data-bobbo-measure-section="' + id + '"]']),
    wall_collection: [
      ['intro', '.bobbo-collection-title'],
      ['filters', '[data-bobbo-replace="toolbar"]'],
      ['designs', '[data-bobbo-replace="products"]'],
    ],
    wall_product: [
      ['gallery', '.reference-product-gallery'],
      ['purchase', '#ReferenceProductForm'],
      ['overview', '.product__description'],
      ['dimensions', '[data-bobbo-size-dimensions]'],
      ['bundle', '[data-reference-bundle]'],
      ['recommendations', 'bobbo-recommendations'],
    ],
    cart: [['cart_items', '.bobbo-cart-lines'], ['cart_summary', '.bobbo-cart-summary']],
  };
  function sectionsFor(doc) {
    const pageType = doc.querySelector('[data-reference-product][data-reference-only="false"]') ? 'wall_product'
      : doc.querySelector('.bobbo-hero') ? 'wall_home'
      : doc.querySelector('[data-bobbo-collection]') ? 'wall_collection'
      : doc.querySelector('body[data-template="cart"]') ? 'cart' : 'other';
    return { pageType, definitions: MAP[pageType] || [] };
  }
  function displayed(win, el) {
    if (!el?.isConnected) return false;
    for (let node = el; node; node = node.parentElement) {
      if (
        node.hidden ||
        node.getAttribute?.("aria-hidden") === "true" ||
        (node.matches?.("dialog") && !node.open)
      )
        return false;
      const css = win.getComputedStyle(node);
      if (
        css.display === "none" ||
        css.visibility === "hidden" ||
        css.visibility === "collapse" ||
        Number(css.opacity) === 0
      )
        return false;
    }
    return true;
  }
  function createCollector(win, options = {}) {
    if (win.BobboMeasurement) return win.BobboMeasurement;
    if (!core || options.enabled !== true) return null;
    const doc = win.document,
      context = sectionsFor(doc);
    if (context.pageType === "other") return null; // No account, checkout, search or contact pages.
    const rawProductId = doc
      .querySelector(
        "[data-reference-product][data-product-id]",
      )
      ?.getAttribute("data-product-id");
    const productId = /^\d{1,20}$/.test(rawProductId || "")
      ? rawProductId
      : null;
    const states = new Map(),
      cleanups = [],
      events = [],
      videos = new Map(),
      selections = new Map();
    let allowed = false,
      destroyed = false,
      lastActivity = win.performance.now();
    let scanned = 0,
      dropped = 0,
      consentEpoch = 0,
      performanceMonitor = null;
    const now = () => win.performance.now();
    function permission() {
      // Dry-run simulation is local-only and can never activate a transport.
      return (
        (options.dryRun === true && options.simulateConsent === true) ||
        (win.navigator.globalPrivacyControl !== true && core.explicitAnalyticsConsent(win.Shopify?.customerPrivacy))
      );
    }
    function synchronize() {
      const next = permission();
      if (next !== allowed) {
        for (const state of states.values()) {
          state.meter.reset();
          state.delivered = false;
        }
        for (const state of videos.values()) state.meter.reset();
        selections.clear();
        consentEpoch++;
        events.length = 0;
        allowed = next;
        lastActivity = now();
      }
      performanceMonitor?.synchronize();
      return allowed;
    }
    function emit(name, fields) {
      if (!synchronize() || destroyed) return false;
      const data = {
        schema_version: "1",
        page_version: "measurement_20260927",
        page_type: context.pageType,
        is_test: options.isTest === true ? 1 : 0,
        ...(productId ? { product_id: productId } : {}),
        ...fields,
      };
      if (options.dryRun === true) {
        events.push({ name, ...data });
        if (events.length > 200) events.shift();
        return true;
      }
      if (win.navigator.onLine === false || typeof win.gtag !== "function") {
        dropped++;
        return false;
      }
      try {
        win.gtag("event", name, {
          ...data,
          send_to: "G-RBBR1YGFZH",
          ...(options.isTest === true ? { debug_mode: true } : {}),
        });
        return true; // Enqueued, NOT proof Google received it.
      } catch (_) {
        dropped++;
        return false;
      }
    }
    function scan() {
      for (const [id, selector] of context.definitions) {
        const element = doc.querySelectorAll(selector)[0];
        const previous = states.get(id);
        if (element && previous?.element !== element) {
          // Theme editor replacement retains instance-level deduplication.
          states.set(id, {
            element,
            meter: previous?.meter || new core.ExposureMeter(),
            delivered: previous?.delivered || false,
          });
        } else if (!element && previous) previous.element = null;
      }
      scanned++;
      for (const [id, selector, kind] of [
        ["bowl_cam_inline", ".bowlcam #camera video", "autoplay"],
        ["bowl_cam_modal", "[data-bowlcam-video-modal] video", "user_play"],
      ]) {
        const element = doc.querySelector(selector),
          previous = videos.get(id);
        if (element && previous?.element !== element)
          videos.set(id, { element, kind, meter: new core.VideoMeter() });
        else if (!element && previous) videos.delete(id);
      }
    }
    function measureVideo(state, time) {
      const video = state.element;
      const visible =
        !doc.hidden &&
        displayed(win, video) &&
        core.isQualified(
          video.getBoundingClientRect(),
          win.innerHeight,
          win.innerWidth,
        );
      const active =
        visible && (!video.paused || video.ended) && !video.seeking;
      const marks = state.meter.sample({
        time,
        position: video.currentTime,
        duration: video.duration,
        active,
        seeking: video.seeking,
        rate: video.playbackRate,
      });
      return { marks, active: active && !video.ended };
    }
    const offerKey = 'bobbo_offer_attempt_v1';
    let offerViewed = false, offerStarted = false, offerOpen = false, offerReturned = false, offerAttempts = 0, offerInvalid = false;
    function clearOfferAttempt() { try { win.sessionStorage.removeItem(offerKey); } catch (_) {} }
    function offerEvent(name, fields = {}) {
      return emit(name, {form_id: 'welcome_offer', section_id: 'newsletter_popup', ...fields});
    }
    function measureOffer() {
      const popup = doc.querySelector('[data-offer-popup]');
      if (!popup) return;
      const visible = popup.open === true && !doc.hidden;
      if (visible && !offerViewed) offerViewed = offerEvent('bobbo_offer_view');
      if (offerOpen && !popup.open) offerEvent('bobbo_offer_dismiss');
      offerOpen = popup.open === true;
      if (offerReturned) return;
      const success = popup.querySelector('[data-offer-success]');
      const error = popup.querySelector('[data-offer-errors]');
      if (!success && !error) return;
      offerReturned = true;
      let pending;
      try { pending = JSON.parse(win.sessionStorage.getItem(offerKey) || 'null'); } catch (_) {}
      clearOfferAttempt();
      // Only Shopify-rendered outcome after this tab's consented attempt qualifies.
      // Refresh/back or an unrelated success URL must not generate another signup.
      const age = Date.now() - pending?.at;
      if (!pending || age < 0 || age > 1800000 || pending.isTest !== (options.isTest === true)) return;
      offerEvent(success ? 'bobbo_offer_success' : 'bobbo_offer_error', {outcome: success ? 'shopify_confirmed' : 'server_rejected'});
    }
    function tick() {
      if (destroyed || !synchronize()) return;
      measureOffer();
      const time = now(),
        active = !doc.hidden && time - lastActivity < 30000;
      const activeUserVideos = [];
      for (const [id, state] of videos) {
        const result = measureVideo(state, time);
        if (state.kind === "user_play" && result.active)
          activeUserVideos.push(state.element);
        for (const milestone of result.marks)
          emit("bobbo_video_progress", {
            video_id: id,
            milestone,
            playback_kind: state.kind,
          });
      }
      const modal = doc.querySelector("dialog[open]");
      for (const [id, state] of states) {
        const userVideo = activeUserVideos.some((video) =>
          state.element?.contains?.(video),
        );
        const unobscured = !modal || modal.contains?.(state.element);
        const qualified =
          (active || userVideo) &&
          unobscured &&
          displayed(win, state.element) &&
          core.isQualified(
            state.element.getBoundingClientRect(),
            win.innerHeight,
            win.innerWidth,
          );
        if (state.meter.sample(time, qualified))
          state.delivered = emit("bobbo_section_view", { section_id: id });
      }
    }
    function flush() {
      if (destroyed || !synchronize()) return;
      for (const [id, state] of states) {
        const delta = state.meter.flush();
        if (delta && state.delivered)
          emit("bobbo_section_engagement", { section_id: id, ...delta });
      }
    }
    function listen(target, name, fn, capture = false) {
      target.addEventListener(name, fn, capture);
      cleanups.push(() => target.removeEventListener(name, fn, capture));
    }
    function activity(event) {
      if (event.isTrusted !== false) lastActivity = now();
    }
    for (const name of ["pointerdown", "keydown", "touchstart", "scroll"])
      listen(doc, name, activity, true);
    listen(doc, "visibilitychange", () => {
      tick();
      if (doc.hidden) {
        performanceMonitor?.flush();
        flush();
      } else lastActivity = now();
    });
    listen(win, "pagehide", () => {
      tick();
      performanceMonitor?.flush();
      flush();
      for (const state of states.values()) state.meter.sample(now(), false);
    });
    listen(win, "pageshow", () => {
      lastActivity = now();
      scan();
      tick();
    });
    listen(doc, "visitorConsentCollected", synchronize);
    listen(doc, "shopify:section:load", () => {
      scan();
      tick();
    });
    listen(doc, "shopify:section:unload", () => {
      tick();
      flush();
      scan();
    });
    for (const eventName of ["seeking", "seeked", "play", "pause", "ended"])
      listen(doc, eventName, () => tick(), true);
    function owner(element) {
      if (element.closest?.('#ReferenceCartDrawer')) return 'cart_drawer';
      if (element.closest?.('#BobboFilter')) return 'filters';
      for (const [id, state] of states)
        if (state.element?.contains?.(element)) return id;
      return "navigation";
    }
    // Capture before storefront handlers navigate or toggle an accordion. Never read free text.
    listen(doc, 'click', event => {
      if (!synchronize()) return;
      const target = event.target?.closest?.('a,button');
      if (!target || target.disabled) return;
      let action = null, destinationPath = null;
      if (target.matches('[data-bundle-add]')) action = 'bundle_add_to_cart';
      else if (target.matches('[data-reference-quickadd],[data-product-url].hdn-qb-wide,[data-product-url].hdn-qb-wide2,[data-product-url].quickbuy__btn')) action = 'quick_add';
      else if (target.matches('[type="submit"]') && target.closest('[data-reference-add]')) action = 'add_to_cart';
      else if (target.matches('[name="checkout"]')) action = 'checkout';
      else if (target.matches('[data-size-guide]')) action = 'size_guide';
      else if (target.matches('[data-bobbo-filter-open]')) action = 'filter_open';
      else if (target.matches('[data-bobbo-clear]')) action = 'filter_clear';
      else if (target.matches('[data-image-zoom]')) action = 'image_zoom';
      else if (target.matches('[data-gallery-thumb],[data-gallery-step]')) action = 'gallery_navigation';
      else if (target.matches('[data-cart-remove]')) action = 'cart_remove';
      else if (target.matches('[data-cart-quantity-step]')) action = 'cart_quantity';
      else if (target.matches('.js-accordion-trigger') && target.closest('[data-bobbo-size-dimensions]') && target.getAttribute('aria-expanded') !== 'true') action = 'dimensions_open';
      else if (target.matches('a') && target.closest('#shopify-section-reference-announcement')) action = 'announcement_link';
      else if (target.matches('a[href]')) {
        try {
          const url = new URL(target.getAttribute('href'), win.location.origin);
          if (url.origin === win.location.origin) {
            if (url.pathname === '/') action = 'home';
            else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/.test(url.pathname)) action = 'checkout';
            else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/.test(url.pathname)) action = 'view_cart';
            else if (/^\/products\/bobbo-[a-z0-9-]+-wall-scratcher$/.test(url.pathname)) action = 'wall_product';
            else if (/^\/collections\/(?:all|art-scratcher|surfboards-scratcher|rockets-scratcher)$/.test(url.pathname)) action = 'collection';
            if (action && action !== 'checkout') destinationPath = url.pathname;
          }
        } catch (_) {}
      }
      if (action) emit('bobbo_cta_click', {section_id: owner(target), cta_id: action, destination_type: action, ...(destinationPath ? {destination_path: destinationPath} : {})});
    }, true);
    listen(doc, 'change', event => {
      if (!synchronize()) return;
      const target = event.target;
      if (target?.matches?.('[data-reference-variant]') && /^\d{1,20}$/.test(target.value)) {
        if (selections.get('variant') === target.value) return;
        selections.set('variant', target.value);
        emit('bobbo_variant_select', {section_id: 'purchase', variant_id: target.value, option_type: 'variant', option_value: 'variant_' + target.value});
      } else if (target?.matches?.('[data-reference-sort]')) {
        const sorts = ['manual','best-selling','title-ascending','title-descending','price-ascending','price-descending','created-ascending','created-descending'];
        if (sorts.includes(target.value)) emit('bobbo_cta_click', {section_id:'filters', cta_id:'sort_change', destination_type:target.value});
      }
    }, true);
    listen(doc, 'submit', event => {
      if (event.target?.matches?.('[data-bobbo-filters]')) emit('bobbo_cta_click', {section_id:'filters', cta_id:'filter_apply', destination_type:'filter_apply'});
      if (!event.target?.matches?.('#BobboOfferSignup') || !synchronize() || offerAttempts >= 10) return;
      measureOffer();
      offerAttempts++;
      const sent = offerEvent('bobbo_offer_submit');
      if (sent && options.dryRun !== true) {
        try { win.sessionStorage.setItem(offerKey, JSON.stringify({at: Date.now(), isTest: options.isTest === true})); } catch (_) {}
      }
    }, true);
    listen(doc, 'input', event => {
      if (!event.target?.matches?.('#BobboOfferEmail') || !synchronize()) return;
      measureOffer();
      if (!offerStarted) offerStarted = offerEvent('bobbo_offer_start');
    }, true);
    listen(doc, 'invalid', event => {
      if (!event.target?.matches?.('#BobboOfferEmail') || offerInvalid || !synchronize()) return;
      offerInvalid = offerEvent('bobbo_offer_error', {outcome: 'browser_validation'});
    }, true);
    listen(doc, 'visitorConsentCollected', () => {
      if (!permission()) { clearOfferAttempt(); offerOpen = false; }
    });
    // FAQ only: dimensions and other accordions must not masquerade as questions.
    listen(doc, 'toggle', event => {
      const target = event.target;
      if (!target?.matches?.('[data-bobbo-faq] details') || !target.open) return;
      const index = [...doc.querySelectorAll('[data-bobbo-faq] details')].indexOf(target);
      if (index >= 0 && index < 20) emit('bobbo_faq_open', {section_id:'faq',faq_id:'faq_' + (index+1)});
    }, true);
    // Observe only same-origin POST /cart/add responses; never inspect response bodies,
    // card data, form values, other URLs, or synthesize a successful add_to_cart.
    if (typeof win.fetch === "function") {
      const original = win.fetch;
      const wrapped = async function (input, init) {
        let cart = false;
        try {
          const raw =
            typeof input === "string" ? input : input?.url || String(input);
          const url = new URL(raw, win.location.origin);
          cart =
            url.origin === win.location.origin &&
            /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/add(?:\.js)?$/.test(
              url.pathname,
            ) &&
            String(init?.method || input?.method || "GET").toUpperCase() ===
              "POST";
        } catch (_) {
          /* Non-cart requests are passed through untouched. */
        }
        const startAllowed = cart && synchronize(),
          epoch = consentEpoch;
        const report = (status) => {
          if (
            !startAllowed ||
            destroyed ||
            !synchronize() ||
            epoch !== consentEpoch
          )
            return;
          const error = core.cartError(status);
          if (error)
            emit("bobbo_add_to_cart_error", {
              error_type: error,
              section_id: "purchase",
            });
        };
        try {
          const response = await original.call(this, input, init);
          if (cart) report(response.status);
          return response;
        } catch (error) {
          if (cart && error?.name !== "AbortError") report(0);
          throw error;
        }
      };
      win.fetch = wrapped;
      cleanups.push(() => {
        if (win.fetch === wrapped) win.fetch = original;
      });
    }
    if (options.performance === true && win.BobboPerformanceAPI) {
      try {
        performanceMonitor = win.BobboPerformanceAPI.createMonitor(win, {
          vitals: win.BobboPerformanceAPI.vitals,
          canSend: permission,
          send: (fields) => emit("bobbo_performance", fields),
        });
      } catch (_) {
        /* Missing/unsupported metric APIs never break the page. */
      }
    }
    scan();
    tick();
    const sampleTimer = win.setInterval(tick, 250);
    const flushTimer = win.setInterval(() => {
      tick();
      flush();
    }, 15000);
    // Loading the platform API does not select or override visitor consent.
    if (!win.Shopify?.customerPrivacy && win.Shopify?.loadFeatures) {
      try {
        win.Shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          (error) => {
            if (!error && !destroyed) synchronize();
          },
        );
      } catch (_) {
        /* Keep permission denied if platform API cannot load. */
      }
    }
    const api = {
      tick,
      flush,
      scan,
      diagnostics: () => ({
        pageVersion: "measurement_20260927",
        isTest: options.isTest === true,
        pageType: context.pageType,
        allowed,
        dryRun: options.dryRun === true,
        sections: context.definitions.map(([id]) => ({
          id,
          found: !!states.get(id)?.element,
          viewed: states.get(id)?.meter.viewed || false,
        })),
        scans: scanned,
        dropped,
        performance: performanceMonitor?.status() || null,
        events: [...events],
      }),
      destroy: () => {
        destroyed = true;
        win.clearInterval(sampleTimer);
        win.clearInterval(flushTimer);
        performanceMonitor?.destroy();
        cleanups.forEach((fn) => fn());
        states.clear();
        videos.clear();
        selections.clear();
        events.length = 0;
        delete win.BobboMeasurement;
      },
    };
    win.BobboMeasurement = api;
    return api;
  }
  return { createCollector, sectionsFor, displayed };
});
